import { OpenPositionType } from "@/api-schema-enums";
import type { OpenPosition } from "@/common/api/open-position/use-open-position-search.query";
import { formatCurrency } from "@/common/helpers/format-currency";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { DetailTabRow } from "@/components/detail-page/_components/tab/detail-tab-row";
import { getCustomerDisplayName } from "@/components/data-table/predefined/customer-table/_helpers/get-customer-display-name";
import { RelationLink } from "@/components/relation-link/relation-link";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { DUNNING_BLOCK_REASON_LABEL_KEYS } from "@/modules/fia/open-position/positions/_helpers/dunning-block-reasons";
import { getDaysOverdue } from "@/modules/fia/open-position/positions/_helpers/get-days-overdue";
import { GavelIcon, ReceiptEuroIcon, UsersIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

export function OverviewTab({ openPosition }: { openPosition: OpenPosition }) {
  const { t } = useTranslation(["fia", "common"]);

  const isPayment = openPosition.type === OpenPositionType.Payment;
  const daysOverdue = openPosition.dueDate ? getDaysOverdue(openPosition.dueDate) : 0;
  const dunningBlock = openPosition.dunningBlock ?? null;

  return (
    <DetailTabGrid>
      <DetailTabCard icon={ReceiptEuroIcon} title={t("fia:DocumentInformation")}>
        <DetailTabRow label={t("fia:InvoiceNumber")} value={openPosition.invoiceNumber} mono />
        <DetailTabRow label={t("fia:DocumentNumber")} value={openPosition.documentNumber} mono />
        <DetailTabRow label={t("fia:DocumentItemNumber")} value={openPosition.documentItemNumber} mono />
        <DetailTabRow
          label={t("fia:FilterType")}
          value={
            <Badge
              variant={BadgeVariant.Outlined}
              colorVariant={isPayment ? BadgeColorVariant.Primary : BadgeColorVariant.Secondary}
              className="rounded-full"
            >
              {t(isPayment ? "fia:Payment" : "fia:Receivable")}
            </Badge>
          }
        />
        <DetailTabRow label={t("fia:FiscalYear")} value={openPosition.fiscalYear} mono />
        <DetailTabRow label={t("fia:CompanyCode")} value={openPosition.companyCode} mono />
      </DetailTabCard>

      <DetailTabCard icon={ReceiptEuroIcon} title={t("fia:AmountInformation")}>
        <DetailTabRow
          label={t("fia:Amount")}
          value={formatCurrency(openPosition.amount, { currency: openPosition.currency })}
        />
        <DetailTabRow label={t("fia:Currency")} value={openPosition.currency} mono />
        <DetailTabRow
          label={t("fia:DueDate")}
          value={
            openPosition.dueDate ? (
              <span className="flex flex-col">
                <span>{formatApiDateOnlyToLocale(openPosition.dueDate)}</span>
                {daysOverdue > 0 && (
                  <span className="text-error text-xs">{t("fia:DaysOverdueValue", { count: daysOverdue })}</span>
                )}
              </span>
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={GavelIcon} title={t("fia:DunningInformation")}>
        <DetailTabRow label={t("fia:DunningLevel")} value={openPosition.dunningLevel} />
        <DetailTabRow
          label={t("fia:DunningReasonColumn")}
          value={
            dunningBlock ? (
              <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Warning}>
                {t(DUNNING_BLOCK_REASON_LABEL_KEYS[dunningBlock.reason])}
              </Badge>
            ) : undefined
          }
        />
      </DetailTabCard>

      <DetailTabCard icon={UsersIcon} title={t("fia:CustomerAndOrganization")}>
        <DetailTabRow
          label={t("common:Customer")}
          value={
            openPosition.customer ? (
              <RelationLink subject="Customer" uuid={openPosition.customer.uuid}>
                {getCustomerDisplayName(openPosition.customer)}
              </RelationLink>
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Company")}
          value={
            openPosition.company ? (
              <RelationLink subject="Company" uuid={openPosition.company.uuid}>
                {openPosition.company.name}
              </RelationLink>
            ) : undefined
          }
        />
        <DetailTabRow
          label={t("common:Branch")}
          value={
            openPosition.branch ? (
              <RelationLink subject="Branch" uuid={openPosition.branch.uuid}>
                {openPosition.branch.name}
              </RelationLink>
            ) : undefined
          }
        />
      </DetailTabCard>
    </DetailTabGrid>
  );
}
